import { FICHE_PAR_DEFAUT, ficheDeLaBulle, type FicheDeBulle } from '@nextilt/commun';
import type { Bulle, FicheEnBase } from '../partage/api/types';
import { ceQuElleAttendSelon } from '../moteur/conditions';
import { delaiEnMots, heureAuxChiffres, jourAbregeDUnInstant, jourEtMoisAbrege, jourLongDUnInstant } from '../partage/datesEnMots';
import { piecesJointes } from './ficheEnMots';
import {
  ADRESSE,
  APRES_TON_GESTE,
  attendQuoi,
  avantLe,
  C_EST_TOI_QUI_L_AS_POSE,
  CONTACT,
  DELAI_D_ATTENTE,
  depuisLe,
  ECHEANCE,
  LE_MONDE_L_A_POSEE,
  leMondeMet,
  LIEN_WEB,
  majuscule,
  RENDEZ_VOUS,
} from '../partage/vocabulaire';

/**
 * Ce que la fiche du téléphone montre sous le titre, sans rendu (chantier
 * `telephone-projets-fiche`, ticket 03, `design/telephone.html`, piste « La fiche » ; décisions
 * d'Igor 14, 15 et 18 de la revue et du cadrage du 2026-10-05).
 *
 * Trois places fixes, sans titre devant : à côté de « ☰ Réglages », ce qu'elle attend ; dessous,
 * la ligne des dates (échéance, rendez-vous, délai) ; puis la ligne des infos (lieu, contact,
 * lien). Une ligne vide n'apparaît pas, l'ordre ne change jamais. La durée, le lieu de travail
 * (« Où ») et l'importance sont des réglages de tri : ils restent derrière « Réglages ».
 *
 * Rien ne se calcule ici sur le moteur : ce qui la retient vient du serveur (`retenuePar`), les
 * dates sont celles qu'on a posées ; on choisit et on met en mots.
 */

export type SorteDePastille = 'echeance' | 'rdv' | 'delai' | 'lieu' | 'contact' | 'lien';

/** Une pastille : son signe, ce qu'elle dit, et ce que la petite feuille montre en entier. */
export interface PastilleDeFiche {
  sorte: SorteDePastille;
  signe: string;
  court: string;
  titre: string;
  entier: string[];
}

/** Une ligne de pastilles, jamais vide. */
export interface LigneDeFiche {
  nom: 'dates' | 'infos';
  pastilles: PastilleDeFiche[];
}

type CeQuiSeMontre = Pick<
  Bulle,
  'dateEcheance' | 'debut' | 'dureeMin' | 'delaiJours' | 'delaiMois' | 'dateAction' | 'lieuTexte' | 'contactNom' | 'contactTel' | 'lien'
>;

/**
 * **Le type et le papier de la fiche au téléphone** (chantier telephone-fiche, modification 01,
 * règle C d'Igor du 2026-10-26) : une bulle à qui personne n'a donné de type, ni elle ni son
 * projet, s'ouvre en Note ; sinon, la règle de l'ordinateur (`ficheDeLaBulle`), qui ne change pas.
 * La feuille donne les fiches du projet défauts compris (`Feuille.fiches`) : un projet resté à
 * Classique sur feuille blanche (`FICHE_PAR_DEFAUT`) n'a rien choisi.
 */
export function ficheAuTelephone(bulle: FicheEnBase, duProjet: FicheDeBulle): FicheDeBulle {
  const projet = { typeParDefaut: duProjet.type, papierParDefaut: duProjet.papier };
  const projetAChoisi = duProjet.type !== FICHE_PAR_DEFAUT.type || duProjet.papier !== FICHE_PAR_DEFAUT.papier;
  if (bulle.typeDeFiche === null && !projetAChoisi) return ficheDeLaBulle({ ...bulle, typeDeFiche: 'note' }, projet);
  return ficheDeLaBulle(bulle, projet);
}

/** Les lignes à montrer, dans leur ordre fixe ; une ligne sans pastille n'y est pas. */
export function lignesDeLaFiche(bulle: CeQuiSeMontre): LigneDeFiche[] {
  const lignes: LigneDeFiche[] = [
    { nom: 'dates', pastilles: lesDates(bulle) },
    { nom: 'infos', pastilles: lesInfos(bulle) },
  ];
  return lignes.filter((l) => l.pastilles.length > 0);
}

function lesDates(bulle: CeQuiSeMontre): PastilleDeFiche[] {
  const dates: PastilleDeFiche[] = [];
  if (bulle.dateEcheance) {
    dates.push({
      sorte: 'echeance',
      signe: '⚑',
      court: `Avant le ${jourEtMoisAbrege(bulle.dateEcheance)}`,
      titre: ECHEANCE,
      entier: [majuscule(avantLe(bulle.dateEcheance)), LE_MONDE_L_A_POSEE],
    });
  }
  if (bulle.debut) {
    const instant = new Date(bulle.debut);
    dates.push({
      sorte: 'rdv',
      signe: '●',
      court: majuscule(`${jourAbregeDUnInstant(instant)} ${heureAuxChiffres(instant)}`),
      titre: RENDEZ_VOUS,
      entier: [
        majuscule(`${jourLongDUnInstant(instant)} à ${heureAuxChiffres(instant)}`) + (bulle.dureeMin ? ` · ${bulle.dureeMin} min` : ''),
        C_EST_TOI_QUI_L_AS_POSE,
      ],
    });
  }
  // Le délai du monde se montre s'il est posé ; le jour du geste seul (posé par l'attente) ne dit rien.
  const delai = delaiEnMots(bulle);
  if (delai !== null) {
    dates.push({
      sorte: 'delai',
      signe: '⧗',
      court: delai,
      titre: DELAI_D_ATTENTE,
      entier: [leMondeMet(delai), ...(bulle.dateAction ? [depuisLe(bulle.dateAction)] : []), APRES_TON_GESTE],
    });
  }
  return dates;
}

function lesInfos(bulle: CeQuiSeMontre): PastilleDeFiche[] {
  const infos: PastilleDeFiche[] = [];
  if (bulle.lieuTexte) {
    infos.push({ sorte: 'lieu', signe: '⌖', court: bulle.lieuTexte, titre: ADRESSE, entier: [bulle.lieuTexte] });
  }
  const contact = [bulle.contactNom, bulle.contactTel].filter((t): t is string => !!t);
  if (contact.length > 0) {
    infos.push({ sorte: 'contact', signe: '☎', court: contact[0] as string, titre: CONTACT, entier: contact });
  }
  // Le même filtre que « Joint » : un lien qui ne s'ouvrirait pas ne se montre pas.
  const lien = piecesJointes(bulle).find((p) => p.sorte === 'lien');
  if (lien && bulle.lien) {
    infos.push({ sorte: 'lien', signe: '↗', court: leSite(bulle.lien), titre: LIEN_WEB, entier: [bulle.lien] });
  }
  return infos;
}

/** Le site d'un lien, sans « www. » : « exemple.fr ». L'adresse entière est dans la petite feuille. */
function leSite(lien: string): string {
  const sansProtocole = lien.replace(/^https?:\/\//i, '');
  const site = sansProtocole.split(/[/?#]/)[0] ?? sansProtocole;
  return site.replace(/^www\./i, '').toLocaleLowerCase('fr-FR') || sansProtocole;
}

/** La pastille « Attend » et la liste qu'elle déplie. */
export interface Attente {
  texte: string;
  bulles: { id: string; titre: string }[];
}

/**
 * Ce qu'elle attend encore, d'après le serveur : les conditions posées à la main et celles
 * d'une bulle qui la contient, pas ce qu'elle contient (on y entre par le ›, décision 15), ni
 * la bulle qui la contient (défaut accepté, décision 20). Une bulle acquise n'attend plus rien.
 */
export function ceQuElleAttend(bulle: Pick<Bulle, 'etat' | 'retenuePar'>): Attente | null {
  const bulles: Attente['bulles'] = ceQuElleAttendSelon(bulle, 'ficheDuTelephone').map((r) => ({ id: r.id, titre: r.titre }));
  const premiere = bulles[0];
  if (!premiere) return null;
  return { texte: attendQuoi(premiere.titre, bulles.length - 1), bulles };
}
